import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Uploads a file to Emergent Object Storage via /api/uploads and returns
 * the public URL (already prefixed with REACT_APP_BACKEND_URL) via onUploaded(url).
 *
 * Props:
 *   - kind: one of "vendor_cover"|"vendor_gallery"|"vendor_logo"|"menu_item"|"verification"|"menu_import"
 *   - accept: HTML accept attribute (defaults to images)
 *   - value: current URL (optional) — shows a small preview + remove button
 *   - onUploaded(url): callback with the resulting URL
 *   - onCleared(): optional callback when the user removes the current image
 *   - label: button text
 *   - testId: base test id (produces `${testId}-input`, `${testId}-clear`)
 */
export default function FileUploader({
  kind,
  accept = "image/png,image/jpeg,image/webp",
  value,
  onUploaded,
  onCleared,
  label = "Upload",
  testId = "file-uploader",
  className = "",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("kind", kind);
      form.append("file", file);
      const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      const abs = data.url.startsWith("http") ? data.url : `${backendUrl}${data.url}`;
      onUploaded?.(abs, data);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const isImage = accept.includes("image");

  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid={testId}>
      {value && isImage && (
        <div className="relative shrink-0">
          <img src={value} alt="" className="w-14 h-14 rounded-lg object-cover border border-[color:var(--border)]" />
          <button
            type="button"
            data-testid={`${testId}-clear`}
            onClick={() => { onCleared?.(); onUploaded?.(""); }}
            className="absolute -top-2 -right-2 bg-white border border-[color:var(--border)] rounded-full p-0.5 hover:bg-[color:var(--surface)]"
            aria-label="Remove"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handle}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <Button
        type="button"
        variant="outline"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="rounded-lg h-10"
      >
        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
        {uploading ? "Uploading…" : label}
      </Button>
    </div>
  );
}
